/**
 * API请求优化工具类
 * 提供请求合并、防抖、批量请求、缓存等功能
 */

class APIOptimizer {
  constructor() {
    this.requestQueue = new Map() // 请求队列，用于合并相同请求
    this.debounceTimers = new Map() // 防抖定时器
    this.cache = new Map() // 内存缓存
    this.batchQueues = new Map() // 批量请求队列
  }

  /**
   * 请求去重和合并
   * 避免同时发起多个相同的请求
   */
  deduplicate(key, requestFn, ttl = 30000) {
    const cacheKey = `dedup_${key}`

    // 检查是否有正在进行的相同请求
    if (this.requestQueue.has(cacheKey)) {
      return this.requestQueue.get(cacheKey)
    }

    // 检查缓存
    if (this.cache.has(key)) {
      const cached = this.cache.get(key)
      if (Date.now() - cached.timestamp < ttl) {
        return Promise.resolve(cached.data)
      }
    }

    // 创建新的请求
    const request = requestFn()
      .then(result => {
        // 缓存结果
        this.cache.set(key, {
          data: result,
          timestamp: Date.now()
        })
        this.requestQueue.delete(cacheKey)
        return result
      })
      .catch(error => {
        this.requestQueue.delete(cacheKey)
        throw error
      })

    this.requestQueue.set(cacheKey, request)
    return request
  }

  /**
   * 防抖请求
   * 避免短时间内频繁发起请求
   */
  debounce(key, requestFn, delay = 300) {
    return new Promise((resolve, reject) => {
      // 清除之前的定时器
      if (this.debounceTimers.has(key)) {
        clearTimeout(this.debounceTimers.get(key))
      }

      // 设置新的定时器
      const timer = setTimeout(async () => {
        try {
          this.debounceTimers.delete(key)
          const result = await requestFn()
          resolve(result)
        } catch (error) {
          reject(error)
        }
      }, delay)

      this.debounceTimers.set(key, timer)
    })
  }

  /**
   * 批量请求
   * 将多个小请求合并为一个大请求
   */
  batchRequest(queueKey, requestFn, batchSize = 10, delay = 100) {
    if (!this.batchQueues.has(queueKey)) {
      this.batchQueues.set(queueKey, {
        requests: [],
        timer: null
      })
    }

    const queue = this.batchQueues.get(queueKey)

    return new Promise((resolve, reject) => {
      queue.requests.push({ resolve, reject, requestFn })

      // 如果达到批量大小，立即执行
      if (queue.requests.length >= batchSize) {
        this._executeBatch(queueKey)
        return
      }

      // 设置延迟执行定时器
      if (queue.timer) {
        clearTimeout(queue.timer)
      }

      queue.timer = setTimeout(() => {
        this._executeBatch(queueKey)
      }, delay)
    })
  }

  async _executeBatch(queueKey) {
    const queue = this.batchQueues.get(queueKey)
    if (!queue || queue.requests.length === 0) return

    const requests = queue.requests.splice(0)
    this.batchQueues.delete(queueKey)

    try {
      // 合并请求数据
      const batchData = requests.map(req => req.requestFn())
      const results = await Promise.allSettled(batchData)

      // 分发结果
      results.forEach((result, index) => {
        const { resolve, reject } = requests[index]
        if (result.status === 'fulfilled') {
          resolve(result.value)
        } else {
          reject(result.reason)
        }
      })
    } catch (error) {
      // 如果批量请求失败，单独处理每个请求
      for (const request of requests) {
        try {
          const result = await request.requestFn()
          request.resolve(result)
        } catch (err) {
          request.reject(err)
        }
      }
    }
  }

  /**
   * 预加载数据
   * 在用户可能需要之前预先加载数据
   */
  preload(key, requestFn, priority = 'low') {
    const priorities = { high: 0, medium: 100, low: 500 }
    const delay = priorities[priority] || 500

    setTimeout(() => {
      if (!this.cache.has(key)) {
        requestFn().then(result => {
          this.cache.set(key, {
            data: result,
            timestamp: Date.now()
          })
        }).catch(() => {
          // 预加载失败不影响主流程
        })
      }
    }, delay)
  }

  /**
   * 智能缓存
   * 根据数据类型和使用频率决定缓存策略
   */
  smartCache(key, data, options = {}) {
    const {
      ttl = 300000, // 默认5分钟
      strategy = 'memory' // memory, localStorage, sessionStorage
    } = options

    const cacheData = {
      data,
      timestamp: Date.now(),
      strategy
    }

    switch (strategy) {
      case 'localStorage':
        try {
          localStorage.setItem(`api_cache_${key}`, JSON.stringify(cacheData))
        } catch (e) {
          // localStorage满了，使用内存缓存
          this.cache.set(key, cacheData)
        }
        break
      case 'sessionStorage':
        try {
          sessionStorage.setItem(`api_cache_${key}`, JSON.stringify(cacheData))
        } catch (e) {
          this.cache.set(key, cacheData)
        }
        break
      default:
        this.cache.set(key, cacheData)
    }
  }

  /**
   * 获取缓存数据
   */
  getCache(key, strategy = 'memory') {
    let cached = null

    switch (strategy) {
      case 'localStorage':
        try {
          const data = localStorage.getItem(`api_cache_${key}`)
          cached = data ? JSON.parse(data) : null
        } catch (e) {
          cached = this.cache.get(key) || null
        }
        break
      case 'sessionStorage':
        try {
          const data = sessionStorage.getItem(`api_cache_${key}`)
          cached = data ? JSON.parse(data) : null
        } catch (e) {
          cached = this.cache.get(key) || null
        }
        break
      default:
        cached = this.cache.get(key) || null
    }

    if (cached && Date.now() - cached.timestamp < (cached.ttl || 300000)) {
      return cached.data
    }

    return null
  }

  /**
   * 清理过期缓存
   */
  cleanExpiredCache() {
    const now = Date.now()

    // 清理内存缓存
    for (const [key, data] of this.cache.entries()) {
      if (now - data.timestamp > (data.ttl || 300000)) {
        this.cache.delete(key)
      }
    }

    // 清理localStorage缓存
    try {
      const keys = Object.keys(localStorage)
      keys.forEach(key => {
        if (key.startsWith('api_cache_')) {
          const data = JSON.parse(localStorage.getItem(key))
          if (now - data.timestamp > (data.ttl || 300000)) {
            localStorage.removeItem(key)
          }
        }
      })
    } catch (e) {
      // 忽略清理错误
    }

    // 清理sessionStorage缓存
    try {
      const keys = Object.keys(sessionStorage)
      keys.forEach(key => {
        if (key.startsWith('api_cache_')) {
          const data = JSON.parse(sessionStorage.getItem(key))
          if (now - data.timestamp > (data.ttl || 300000)) {
            sessionStorage.removeItem(key)
          }
        }
      })
    } catch (e) {
      // 忽略清理错误
    }
  }

  /**
   * 销毁优化器，清理所有资源
   */
  destroy() {
    // 清除所有定时器
    for (const timer of this.debounceTimers.values()) {
      clearTimeout(timer)
    }

    // 清除批量请求定时器
    for (const queue of this.batchQueues.values()) {
      if (queue.timer) {
        clearTimeout(queue.timer)
      }
    }

    // 清空所有数据
    this.requestQueue.clear()
    this.debounceTimers.clear()
    this.cache.clear()
    this.batchQueues.clear()
  }
}

// 创建全局实例
const apiOptimizer = new APIOptimizer()

// 定期清理过期缓存
setInterval(() => {
  apiOptimizer.cleanExpiredCache()
}, 60000) // 每分钟清理一次

export default apiOptimizer